import { useEffect, useState } from 'react';
import api from '@/api/client';

/**
 * Environment-level config fetched from /api/config at app boot.
 * Cached in a module-level variable so we only hit the endpoint once
 * per page load, regardless of how many components use this hook.
 */
export interface AppConfig {
  version: string;
  devTools: boolean;
  devLogin?: boolean;
  environment: string;
}

let cached: AppConfig | null = null;
let inflight: Promise<AppConfig> | null = null;

async function load(): Promise<AppConfig> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = api.get<AppConfig>('/config').then((r) => {
    cached = r.data;
    inflight = null;
    return r.data;
  }).catch(() => {
    inflight = null;
    return { version: '', devTools: false, devLogin: false, environment: 'unknown' };
  });
  return inflight;
}

export function useAppConfig(): AppConfig | null {
  const [config, setConfig] = useState<AppConfig | null>(cached);
  useEffect(() => {
    if (cached) return;
    load().then(setConfig);
  }, []);
  return config;
}
